import React, { forwardRef, useState, useContext, useEffect } from 'react';
import classes from './ActionMenuDropdown.module.scss';
import Button from '@/components/Button/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import ListGroup from 'react-bootstrap/ListGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { viewportContext } from '@/context/viewport.context';

const MenuButton = forwardRef(({ styles, children, onClick, additionalClasses, ...props }, ref) => {
  return (
    <Button
      type={styles.type}
      text={styles.text}
      shape={styles.shape}
      border={styles.border}
      ref={ref}
      icon={{ style: styles.icon?.style, font: styles.icon?.font, position: styles.icon?.position }}
      additionalClasses={additionalClasses}
      styles={{ width: styles.width }}
      onButtonClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      {children}
    </Button>
  );
});
MenuButton.displayName = 'MenuButton';

const ActionMenuDropdown = ({ menuList = [], buttonStyles = {}, onSelect, additionalClasses = '', ...props }) => {
  const { isMobileOrTablet } = useContext(viewportContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [activeSubmenuKey, setActiveSubmenuKey] = useState(null);

  const styles = {
    type: buttonStyles.buttonType || 'default',
    text: buttonStyles.buttonText || '',
    shape: buttonStyles.buttonShape || 'circle',
    border: buttonStyles.buttonBorder || false,
    icon: buttonStyles.buttonIcon || { style: 'fas', font: 'ellipsis' },
    width: buttonStyles.buttonWidth || 'auto'
  };

  const activeParent = activeSubmenuKey ? menuList.find((menuItem) => menuItem.key === activeSubmenuKey) : null;
  const currentMenuList = activeParent ? activeParent.children || [] : menuList;

  const selectItem = (menuItem) => {
    if (menuItem.children?.length) {
      setActiveSubmenuKey(menuItem.key);
      return;
    }
    if (menuItem.back) {
      setActiveSubmenuKey(null);
      return;
    }
    setActiveSubmenuKey(null);
    onSelect(menuItem.key);
  };

  const toggleMobileMenu = (e) => {
    setMobileOpen((prevState) => {
      if (prevState) {
        document.body.classList.remove(classes.noScroll);
        setActiveSubmenuKey(null);
      } else {
        document.body.classList.add(classes.noScroll);
      }
      return !prevState;
    });
  };

  // toggleMobileMenu is the only thing that clears noScroll, and it never runs if the component
  // goes away with the sheet open -- a client-side route change, or a keyed parent remounting
  // on navigation. Without this the next page loads unscrollable with nothing on screen to fix it.
  useEffect(() => {
    return () => document.body.classList.remove(classes.noScroll);
  }, []);

  return (
    <>
      {!isMobileOrTablet && (
        <Dropdown
          align="end"
          className={`action-menu-dropdown ${classes.actionMenuDropdown} ${additionalClasses}`}
          show={desktopOpen}
          autoClose="outside"
          onToggle={(nextShow) => {
            setDesktopOpen(nextShow);
            if (!nextShow) setActiveSubmenuKey(null);
          }}
          onSelect={(eventKey) => {
            const menuItem = currentMenuList.find((item) => item.key === eventKey);
            if (!menuItem) return;
            if (!menuItem.children?.length && !menuItem.back) setDesktopOpen(false);
            selectItem(menuItem);
          }}
        >
          <Dropdown.Toggle as={MenuButton} styles={styles}></Dropdown.Toggle>
          <Dropdown.Menu className={classes.actionMenu}>
            {currentMenuList.map((menuItem, index) => {
              if (menuItem.hidden) {
                return <React.Fragment key={menuItem.key}></React.Fragment>;
              }
              const menuItemClass = menuItem.classes ? menuItem.classes : classes.menuItemDefaultClass;
              return (
                <Dropdown.Item key={menuItem.key} className={`${classes.menuItem} ${menuItemClass} ${classes[menuItem.key]}`} eventKey={menuItem.key}>
                  <span className={classes.itemLabel}>
                    {menuItem.icon && (
                      <span className={classes.itemIcon}>
                        <FontAwesomeIcon icon={[menuItem.icon.style, menuItem.icon.font]} />
                      </span>
                    )}
                    {menuItem.text && <span data-cy={`dropdown-menu-item-${menuItem.text}`}>{menuItem.text}</span>}
                  </span>
                  {menuItem.children?.length > 0 && (
                    <span className={classes.itemCaret}>
                      <FontAwesomeIcon icon={['fas', 'caret-right']} />
                    </span>
                  )}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      )}
      {isMobileOrTablet && (
        <>
          <MenuButton onClick={toggleMobileMenu} styles={styles} additionalClasses={additionalClasses} />
          {mobileOpen && (
            <>
              <div className={classes.overlay} onClick={toggleMobileMenu}></div>
              <ListGroup className={classes.mobileActionMenu}>
                {currentMenuList.map((menuItem, index) => {
                  if (menuItem.hidden) {
                    return <React.Fragment key={menuItem.key}></React.Fragment>;
                  }
                  const listItemClass = menuItem.classes ? menuItem.classes : classes.listItemDefaultClass;
                  return (
                    <ListGroup.Item
                      key={menuItem.key}
                      action
                      onClick={() => {
                        if (!menuItem.children?.length && !menuItem.back) toggleMobileMenu();
                        selectItem(menuItem);
                      }}
                      className={`${classes.listItem} ${listItemClass} ${classes[menuItem.key]}`}
                    >
                      <span className={classes.itemLabel}>
                        {menuItem.icon && (
                          <span className={classes.itemIcon}>
                            {menuItem.icon.style && menuItem.icon.font && <FontAwesomeIcon icon={[menuItem.icon.style, menuItem.icon.font]} />}
                          </span>
                        )}
                        {menuItem.text && <span>{menuItem.text}</span>}
                      </span>
                      {menuItem.children?.length > 0 && (
                        <span className={classes.itemCaret}>
                          <FontAwesomeIcon icon={['fas', 'caret-right']} />
                        </span>
                      )}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </>
          )}
        </>
      )}
    </>
  );
};

export default ActionMenuDropdown;
